import React, { useState } from 'react';
import SidePanelContentSection from '../../../common2/side-panel/SidePanelContentSection';
import Column from '../../../common/layout/Column';
import { ClaimContext } from '../types';
import { ClaimStepContainerProps } from '../common/ClaimStepContainer';
import RichLocale from '../../../common2/RichLocale';
import Button from '../../../common2/Button';
import { useMutation, UseQueryResult } from '@tanstack/react-query';
import container from '../../../../container';
import { Select } from '../../../common2/Select/Select';
import LinkPreview from '../../../common/LinkPreview';
import Locale from '../../../Translate';
import Text from '../../../common/Text/Text';
import Heading3 from '../../../common/Heading3';
import { languageLabels } from '../claimsHelpers';
import styles from './DORSigningLinkStep.scss';
import ColumnsLayout from '../../../common/layout/ColumnsLayout';
import ConsumerActionStatusBadge from '../../../claims-center/ClaimsTable/ConsumerActionStatusBadge';

type ClaimContextForDORSigningLinkStep = Pick<
  ClaimContext,
  | 'shipmentId'
  | 'recipientEmail'
  | 'carrierCode'
  | 'carrierCountryCode'
  | 'availableDORLanguages'
  | 'consumerActionStatus'
  | 'shouldForceShowEDORStep'
>;

export type DORSigningLinkStepProps = {
  claim: ClaimContextForDORSigningLinkStep;
  as: React.ComponentType<ClaimStepContainerProps>;
  shopId: number;
  availableDorLanguagesQueryResult: UseQueryResult<string[]>;
} & ClaimStepContainerProps;

export const mapAvailableLanguages = (availableDORLanguages: string[] = []) =>
  availableDORLanguages
    .map((language) => ({
      value: language,
      label: languageLabels[language] || language.toUpperCase(),
    }))
    .sort((a, b) => a.value.localeCompare(b.value));

function DORSigningLinkStep(props: DORSigningLinkStepProps) {
  const {
    claim,
    as: Container,
    isExtractingInvoice,
    isFailedToExtractInvoice,
    extractInvoiceData,
    shopId,
    availableDorLanguagesQueryResult,
    ...restProps
  } = props;

  const {
    shipmentId,
    recipientEmail,
    carrierCode,
    carrierCountryCode,
    consumerActionStatus,
    shouldForceShowEDORStep,
  } = claim;

  if (!shipmentId || !carrierCode || !carrierCountryCode) {
    throw new Error('The necessary data for the DOR step is not available');
  }

  const [language, setLanguage] = useState('');

  const mutation = useMutation({
    mutationKey: ['createDorSigningLink', shopId, shipmentId, language],
    mutationFn: (language: string) =>
      container.touchpointsApi.createDorSigningLink(shopId, shipmentId, language, recipientEmail),
  });

  const StatusDisplay = () => (
    <div className={styles.consumerActionStatusContainer}>
      <Text>
        <Locale>Status</Locale>
      </Text>
      <ConsumerActionStatusBadge consumerActionStatus={consumerActionStatus} />
    </div>
  );

  return (
    <Container {...restProps} isDisabled={mutation.isLoading}>
      <SidePanelContentSection heading="E_SIGNATURE_DOR_SECTION_HEADER" />
      <RichLocale ink>E_SIGNATURE_DOR_SECTION_INFO_RICHTEXT</RichLocale>
      <Column>
        <div className={styles.signingLinkContainer}>
          <Heading3>GENERATE_DOR_LINK_FORM_HEADER</Heading3>
          {consumerActionStatus && shouldForceShowEDORStep && <StatusDisplay />}
          {consumerActionStatus && !shouldForceShowEDORStep ? (
            <StatusDisplay />
          ) : (
            <>
              <ColumnsLayout>
                <Select
                  name="language"
                  label="Language"
                  placeholder="Select a language"
                  options={mapAvailableLanguages(availableDorLanguagesQueryResult.data)}
                  isRequired
                  value={language}
                  onChange={setLanguage}
                  noErrorTranslation
                />
                <div className={styles.signingLinkButtonContainer}>
                  <Button
                    disabled={!language || mutation.isLoading}
                    name="generate-dor-link-button"
                    onClick={() => mutation.mutate(language)}
                    size="medium"
                  >
                    GENERATE_DOR_LINK_BUTTON_TEXT
                  </Button>
                </div>
              </ColumnsLayout>
              <Text className="ink">
                <Locale>SEND_DOR_LINK_TO_CUSTOMER</Locale>
              </Text>
              <div className={styles.signingLink}>
                <LinkPreview
                  showCopyButton={mutation.isSuccess}
                  isLoading={mutation.isLoading}
                  isError={mutation.isError}
                  errorText="LINK_PREVIEW_DOR_ERROR"
                  loadingText="LINK_PREVIEW_DOR_LOADING"
                  placeholder="LINK_PREVIEW_DOR_PLACEHOLDER"
                >
                  {mutation.data?.signingLink}
                </LinkPreview>
              </div>
            </>
          )}
        </div>
      </Column>
    </Container>
  );
}

export default React.memo(DORSigningLinkStep);
